import { cva } from 'class-variance-authority';

/**
 * Das Eingabefeld aus shadcn/ui — als KLASSENSATZ und nicht als Bauteil.
 *
 * **Warum kein `<Input>`-Bauteil.** Dreissig Seiten schreiben heute ihr eigenes
 * `<input className={INPUT_CLASS}>`, und dieselbe Klasse trägt auch jedes
 * `<select>` und jedes `<textarea>` dieser Anwendung — dreierlei Elemente, die
 * ein Bauteil namens `Input` nicht abdeckt. Ein Bauteil, das nichts täte, als
 * eine Klasse zu setzen, wäre ein dritter Name für dieselbe Sache; und die
 * Auswahlfelder MÜSSEN echte `<select>` bleiben, weil vierzehn Prüfungen
 * `selectOption` darauf fahren (siehe `utils.ts`).
 *
 * **Deshalb keine feste Höhe, sondern `min-h-[34px]`.** Die Vorlage setzt
 * `h-9`. Ein `<textarea rows={4}>` mit fester Höhe wäre einzeilig — und die
 * Storno-Begründung ist genau so ein Feld. Die Zahl selbst ist seit
 * Design-Annäherung Spec 5.5 / Task `V-restseiten` **34 px** und nicht mehr
 * 32 px («Höhen: Header-Buttons und Inputs 34px», `DESIGN_SYSTEM.md` §3) —
 * `min-` bleibt, nur die Untergrenze wandert.
 *
 * Übersetzt gegenüber der Vorlage (Tailwind 4 → 3.4), Einzelheiten in
 * `utils.ts`: `shadow-xs` → `shadow-sm`, die `aria-invalid:`-Kurzform entfällt,
 * und `bg-transparent dark:bg-input/30` wird zum eigenen Token `bg-field`.
 *
 * **`bg-field` statt durchsichtig.** Im Dunkeln ist das Feld HELLER als sein
 * Kasten (`slate-800` auf `slate-900`), im Hellen weiss auf grau — ein Feld ist
 * eine Vertiefung, in die man schreibt, und was man an ihm erkennen muss, ist
 * seine Ausdehnung. Durchsichtig hätte es keine. Das gesperrte Feld geht
 * dagegen auf `bg-muted` und rückt damit an seinen Kasten heran.
 *
 * **`border-strong` statt `border-input` seit Design-Annäherung Spec 5.4,
 * Befund 3 / Task `V-zustaende` Schritt 3.** Der eine Ort, an dem eine Kante
 * die WCAG-Grenze allein trägt, ist genau dieses Feld auf einer weissen
 * Karte — hell `--field` (weiss) auf `--card` (weiss), Flächenunterschied
 * null. `--border` misst dort **1,25:1**, `--fa-border-strong` **1,49:1**.
 * Das ist eine Verbesserung und **keine** Lösung; `W-borderkontrast` bleibt
 * offen, die 3:1 sind nicht erreicht. Aufgeschrieben, damit niemand aus dem
 * höheren Wert eine Behebung liest.
 */
export const inputVariants = cva(
  'w-full min-h-[34px] min-w-0 rounded-md border border-strong bg-field px-2.5 py-1.5 ' +
    'text-base md:text-sm text-foreground shadow-sm transition-[color,box-shadow] outline-none ' +
    'placeholder:text-muted-foreground ' +
    'selection:bg-primary selection:text-primary-foreground ' +
    // `focus:` und nicht `focus-visible:` wie in der Vorlage. Dieselbe Klasse
    // trägt auch jedes `<select>`, und ein mit der Maus geöffnetes
    // Auswahlfeld erfüllt `:focus-visible` nicht in jedem Browser — der Ring
    // fehlte dann genau dort, wo gerade etwas ausgewählt wird.
    'focus:border-ring focus:ring-2 focus:ring-ring/40 ' +
    'disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground',
);
